import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import SpritzBadge from "@/components/SpritzBadge";
import MusicToggle from "@/components/MusicToggle";

const HERO_IMG = "/photos/hero.jpg";
const EASE = [0.16, 1, 0.3, 1];

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  animate: { opacity: 1, y: 0 },
};

export default function Hero({ onReserve, onExplore }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-4%", "8%"]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen items-end overflow-hidden"
    >
      <motion.div style={{ y }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Table dressée à la lueur des bougies, en soirée"
          className="h-full w-full scale-[1.15] object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/55 to-ink/25" />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 55% 45% at 72% 28%, rgba(212,175,55,0.16), transparent 70%)",
          }}
        />
      </motion.div>

      <SpritzBadge />
      <MusicToggle />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-16 pt-36 md:px-10 md:pb-24">
        <motion.p
          {...fadeUp}
          transition={{ delay: 0.25, duration: 0.9, ease: EASE }}
          className="text-xs uppercase tracking-[0.3em] text-gold/90"
        >
          Toulouse · Cinq maisons All For You · Ouvertes ce soir
        </motion.p>

        <h1 className="font-display mt-6 text-[clamp(3rem,10.5vw,8.5rem)] font-medium leading-[0.95] tracking-tight text-bone">
          {["La nuit", "vous appartient."].map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.4 + i * 0.16, duration: 1.05, ease: EASE }}
              >
                {i === 1 ? (
                  <>
                    vous{" "}
                    <em className="font-accent italic text-gold">appartient.</em>
                    <img
                      src="/logos/all4you.png"
                      alt="All For You"
                      data-testid="hero-all4you-logo"
                      className="ml-6 hidden h-[0.5em] w-auto sm:inline-block"
                      style={{ filter: "invert(1)" }}
                    />
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.img
          src="/logos/all4you.png"
          alt="All For You"
          data-testid="hero-all4you-logo-mobile"
          className="mt-5 h-9 w-auto sm:hidden"
          style={{ filter: "invert(1)" }}
          {...fadeUp}
          transition={{ delay: 1.2, duration: 0.9, ease: EASE }}
        />

        <motion.p
          {...fadeUp}
          transition={{ delay: 0.9, duration: 0.9, ease: EASE }}
          className="mt-8 max-w-xl text-base leading-relaxed text-zinc-300 md:text-lg"
        >
          Nos établissements sont ouverts et en dehors de la zone de
          couvre-feu à Toulouse. Le Lillet Spritz est offert le soir pour
          votre repas pendant toute la durée du couvre-feu !
        </motion.p>

        <motion.div
          {...fadeUp}
          transition={{ delay: 1.05, duration: 0.9, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={onReserve}
            data-testid="hero-reserve-button"
            className="rounded-full bg-gold px-8 py-4 text-xs font-medium uppercase tracking-[0.25em] text-ink transition-colors hover:bg-champagne"
          >
            Réserver une table
          </button>
          <button
            onClick={onExplore}
            data-testid="hero-explore-button"
            className="inline-flex items-center gap-3 rounded-full border border-white/20 px-8 py-4 text-xs uppercase tracking-[0.25em] text-bone transition-colors hover:border-gold hover:text-gold"
          >
            Découvrir les maisons <ArrowDown size={15} />
          </button>
        </motion.div>

        <motion.div
          {...fadeUp}
          transition={{ delay: 1.15, duration: 0.9, ease: EASE }}
          className="mt-6 inline-flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-gold/80"
          data-testid="hero-spritz-note"
        >
          <img
            src="/logos/lillet.png"
            alt="Lillet"
            className="h-5 w-auto"
            data-testid="hero-lillet-logo"
          />
          Spritz offert · Sur présentation du SMS
        </motion.div>
      </div>
    </section>
  );
}
